import { useShouldShowReviewSnippets } from '@client/hooks/use-review-snippets-on-vendor-storefronts';
import { useIsMarkAsBookedLiteStorefrontsEnabled } from '@client/hooks/useMarkAsBookedLiteStorefronts';
import { headingTagOneAtom } from '@client/jotai/shared/seo-experiment';
import { AboutHighlights } from '@components/about-highlights/about-highlights';
import { ratingAtom, totalCountAtom } from '@components/reviews/atoms';
import { useIsNew } from '@hooks/use-is-new';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useStorefrontPeaceOfMind } from '@hooks/use-storefront-peace-of-mind';
import { useScreenDetails } from '@hooks/useScreenDetails';
import { PeaceOfMindHeader } from '@pages/Storefront/components/peace-of-mind/PeaceOfMindHeader';
import { useAppSelector } from '@redux/hooks';
import { selectIsMobile } from '@redux/viewport/selectors';
import RfqModal from '@tkmp/rfq/components/RfqModal/RfqModal';
import { useInlineRfqForm } from '@tkmp/rfq/hooks/useInlineRfqForm/useInlineRfqForm';
import RFQButton from '@tkmp/rfq/shared/cta_box/RfqButton';
import { TextEditorial, TextHero } from '@tkww/orion-web-ds-text'; // UnionHero
import classnames from 'classnames';
import { useAtomValue } from 'jotai';
import React, { type FC, useMemo } from 'react';
import type { Decorated, Raw } from 'types/vendor';
import { HostedBadge } from '../../components/HostedBadge/HostedBadge';
import { ReviewSnippets } from '../../components/ReviewSnippets/ReviewSnippets';
import ContactButtons from '../../components/contact_buttons';
import SocialSharing from '../../components/social_sharing';
import Social from '../contact/components/social';
import Styles from './VendorInfo.scss';
import BookVendor from './components/BookVendor';
import { useIsVendorBooked } from './components/BookVendor/hooks/use-is-vendor-booked';
import Address from './components/address';
import { BannerInfo } from './components/banner_info';
import FavoritesButton from './components/favorites_button/FavoritesButton';
import ReviewSummary from './components/review_summary';

interface VendorInfoProps {
	isNavSticky?: boolean;
}

const VendorInfo: FC<VendorInfoProps> = (props) => {
	const { isNavSticky } = props;
	const vendor = useAppSelector((state) => state.vendor.vendor as Decorated);
	const vendorRaw = useAppSelector((state) => state.vendor.vendorRaw as Raw);
	const membership = useAppSelector((state) => state.membership);
	const isMobile = useAppSelector(selectIsMobile);
	const isLiteStorefront = useLiteStorefront();
	const headingTagOneFromExperiment = useAtomValue(headingTagOneAtom);
	const isNew = useIsNew();
	const bookings = useAppSelector((state) => state.bookings.bookings);
	const isVendorBooked = useIsVendorBooked(bookings, vendor.id);
	const isMarkAsBookedLiteStorefrontsEnabled =
		useIsMarkAsBookedLiteStorefrontsEnabled();

	const [isLandscape] = useScreenDetails();
	const rating = useAtomValue(ratingAtom);
	const totalCount = useAtomValue(totalCountAtom);
	const hideFavorite = useMemo(
		() => isMobile && !isLandscape,
		[isMobile, isLandscape],
	);

	const isBookedButtonEnabled =
		(vendor.isPaid || isMarkAsBookedLiteStorefrontsEnabled) && !isMobile;

	const showFavoriteButtonOnExperiment = useMemo(() => {
		return isBookedButtonEnabled ? !isVendorBooked : true;
	}, [isBookedButtonEnabled, isVendorBooked]);

	const formContext = useInlineRfqForm({
		initiator: 'Lite Storefront Header',
		headerText: 'Get a personalized quote',
		subheaderText:
			vendor.categoryCode === 'BWP'
				? 'Contact for Appointment'
				: 'Contact for Pricing',
	});
	const headingTagOne =
		headingTagOneFromExperiment === null
			? vendor?.name
			: headingTagOneFromExperiment;

	const shouldShowRecentlyUpdatedBanner = useMemo(() => {
		return (
			vendorRaw?.freshContent === true && !isLiteStorefront && !vendorRaw.isNew
		);
	}, [vendorRaw?.freshContent, isLiteStorefront, vendorRaw.isNew]);

	const getFavoritesContainerClasses = () => {
		const styles = [];

		if (vendor?.isPaid) {
			styles.push(Styles.favoritesButtonContainer);
		} else {
			styles.push(Styles.favoritesButtonUnpaidContainer);
		}

		if (isMobile) {
			styles.push(Styles.favoritesButtonContainerGallery);
		}

		return classnames(...styles);
	};

	const reviewStarSize = 'md';
	const isStorefrontPeaceOfMind = useStorefrontPeaceOfMind();
	const showReviewSnippets = useShouldShowReviewSnippets();

	const getContactOrSocial = () => {
		if (!isLiteStorefront) {
			return null;
		}
		const socialOrLinks = (
			<Social social={vendor.social} vendor={vendor} iconSize="md" />
		);
		return !isMobile ? socialOrLinks : <ContactButtons />;
	};

	return (
		<div id="section-vendor-info" className={Styles.sectionVendorInfo}>
			<div className={Styles.vendorNameFavoritesContainer}>
				<div className={Styles.vendorNameContainer}>
					{isLiteStorefront ? (
						<h1 className={Styles.vendorName}>{headingTagOne}</h1>
					) : (
						<TextHero as="h1" size="sm">
							{headingTagOne}
						</TextHero>
					)}
				</div>
				<div className={Styles.buttonsContainer}>
					{!hideFavorite && !isNavSticky && (
						<SocialSharing vendor={vendor} icon="share-ios" />
					)}
					{!hideFavorite && !isNavSticky && showFavoriteButtonOnExperiment && (
						<div className={getFavoritesContainerClasses()}>
							<FavoritesButton
								membership={membership}
								vendor={vendor}
								snackbarArrowPosition="center"
							/>
						</div>
					)}
					{isBookedButtonEnabled && !isNavSticky && <BookVendor />}
				</div>
			</div>
			{!isLiteStorefront && <Headline headline={vendor?.headline} />}
			<div className={Styles.vendorRatingAddressWrapper}>
				<ReviewSummary
					vendor={vendor}
					rating={rating}
					totalCount={totalCount}
					extendedBow={true}
					starSize={reviewStarSize}
					showLabelCount={!isLiteStorefront}
					showBow={isLiteStorefront}
					showNew={isNew}
					collapseStarRating={showReviewSnippets}
				/>
				<Address
					vendor={vendor}
					showIcon={!isLiteStorefront}
					link={!isLiteStorefront}
					showAddress={isLiteStorefront}
				/>
				<HostedBadge />
				{isStorefrontPeaceOfMind && (
					<div className={Styles.badgeWrapper}>
						<PeaceOfMindHeader />
					</div>
				)}
				{getContactOrSocial()}
				{isLiteStorefront && (
					<div className={Styles.rfqWrapper}>
						<RfqModal context={formContext} />
						<RFQButton
							type="message"
							color="secondary"
							size="md"
							label="Request quote"
							handleCTAClick={formContext.openModal}
						/>
					</div>
				)}
			</div>
			{shouldShowRecentlyUpdatedBanner && (
				<BannerInfo
					label={'Recently updated'}
					info={'This vendor updated their storefront within the last 15 days.'}
					className={Styles.bannerInfo}
				/>
			)}
			<AboutHighlights />
			{showReviewSnippets && <ReviewSnippets />}
		</div>
	);
};

const Headline = ({ headline }: { headline: string | null }) => {
	if (!headline) {
		return null;
	}

	return (
		<TextEditorial className={Styles.headline} bold>
			{headline}
		</TextEditorial>
	);
};

export default VendorInfo;
